import { Component } from '@theme/component';

/**
 * Features section — swaps its content for the product an emitter names.
 *
 * The section's own markup carries `bind:` attributes and one `each` template, so a swap is
 * data bound into markup that is already there rather than markup built here. The data is
 * that product's config as JSON, from `templates/product.features-json.liquid`.
 *
 * A Section Rendering request would be the obvious way to do this and does not work: it
 * carries this section's own product setting, and the setting wins over the url's product,
 * so every swap would render the same thing.
 *
 * The contract is named for this component rather than for whoever asks, so any section
 * can drive it.
 *
 * `productUrls` and `styleNames` (one per selection) and `selection` (the 1-based one in play)
 * are optional, for an element with `data-selection` that follows one pair of a matching set.
 * When its pair comes into play it fires a bubbling `reveal:request`, and when its style changes
 * a `relabel:request` carrying the name — both of which a tabbed section answers.
 *
 * @example
 * element.dispatchEvent(
 *   new CustomEvent('features:update', {
 *     bubbles: true,
 *     detail: { productUrl, productUrls, styleNames, selection },
 *   }),
 * );
 */
class FeaturesSection extends Component {
  /** @type {AbortController | null} */
  #pending = null;

  /** @type {string | null} The product on screen, so the same one is not fetched twice. */
  #shown = null;

  /** @type {number | undefined} The selection last announced as in play. */
  #inPlay;

  /** @type {string | null | undefined} The style name last asked for, so it is asked once. */
  #styleName;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('features:update', this.#update);

    // Whoever drives this section may have announced its selection before this element
    // upgraded — a url or a preselected style is applied on their connect. So ask.
    document.dispatchEvent(new CustomEvent('features:request'));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('features:update', this.#update);
    this.#pending?.abort();
  }

  /** @param {Event} event */
  #update = async (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail ?? {};

    // An emitter that named no section is addressing all of them.
    if (detail.target && detail.target !== this.dataset.eventId) return;

    // Following one pair of a matching set rather than whichever is in play.
    const selection = Number(this.dataset.selection);

    // Its pair came into play, so whatever holds it — a tab — can bring it forward. Only on
    // the change, or a style pick would undo a tab the shopper opened themselves.
    const cameIntoPlay = detail.selection !== this.#inPlay && detail.selection === selection;
    this.#inPlay = detail.selection;
    if (selection && cameIntoPlay) this.#askHost('reveal:request');

    // Named for its pair's style, once one is picked; null asks for the label it started with.
    const styleName = selection ? (detail.styleNames?.[selection - 1] ?? null) : undefined;
    if (styleName !== undefined && styleName !== this.#styleName) {
      this.#styleName = styleName;
      this.#askHost('relabel:request', { label: styleName });
    }

    const productUrl = selection ? detail.productUrls?.[selection - 1] : detail.productUrl;
    if (!productUrl) return;

    // Already showing it: an emitter is free to say so as often as it likes.
    if (productUrl === this.#shown) return;

    // A section or a block, whichever rendered this element beside its content.
    const root = this.parentElement;
    if (!root) return;

    // Clicking through styles outruns the network, and a stale answer would win.
    this.#pending?.abort();
    const controller = new AbortController();
    this.#pending = controller;

    try {
      const url = new URL(productUrl, window.location.origin);
      url.search = '';
      // The alternate view that renders a product's PDP Feature config as JSON.
      url.searchParams.set('view', 'features-json');

      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) return;

      const data = await response.json();
      // A product with no config of its own would empty the section, which is worse than
      // leaving the last one up.
      if (!data?.banners?.length) return;

      const slideshow = root.querySelector('slideshow-component');

      this.render(data, root);
      this.#shown = productUrl;

      // re-mount
      slideshow?.updatedCallback();
      slideshow?.scrollToStart();
    } catch {
      // An aborted or failed request leaves the section showing what it had.
    }
  };

  /**
   * Ask whatever holds this — a tab — to act, once it can hear.
   *
   * Modules run in document order and a tabbed section's loads after its panels, so on a
   * restored url the builder answers before the host has upgraded, and the request is lost.
   *
   * @param {string} type
   * @param {object} [detail]
   */
  async #askHost(type, detail) {
    const pending = [];
    for (let node = this.parentElement; node; node = node.parentElement) {
      if (node.matches(':not(:defined)')) pending.push(customElements.whenDefined(node.localName));
    }
    await Promise.all(pending);

    this.dispatchEvent(new CustomEvent(type, { bubbles: true, detail }));
  }
}

if (!customElements.get('features-section')) {
  customElements.define('features-section', FeaturesSection);
}
